import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from 'platform/forms-system/src/js/actions';

import { isValidDate } from './utils';
import {
  clearSavedSeparationDate,
  saveSavedSeparationDate,
} from './screenerSession';

const isAddedServicePeriod = ({ dateRange, serviceBranch } = {}) =>
  !dateRange?.from && !serviceBranch && Boolean(dateRange?.to);

/**
 * Drops what the form's first page adds to the form data for a BDD claim: the
 * `view:isBddData` flag and the empty service period holding the separation
 * date. Returns the same object when there is nothing to drop.
 * @param {Object} formData - form data
 * @returns {Object} form data without the BDD additions
 */
export const removeBddFormData = formData => {
  if (!formData) {
    return formData;
  }

  const periods = formData.serviceInformation?.servicePeriods;
  const keptPeriods = Array.isArray(periods)
    ? periods.filter(period => !isAddedServicePeriod(period))
    : periods;
  const hasFlag = 'view:isBddData' in formData;
  const droppedPeriods =
    Array.isArray(periods) && keptPeriods.length !== periods.length;

  if (!hasFlag && !droppedPeriods) {
    return formData;
  }

  const cleaned = { ...formData };
  delete cleaned['view:isBddData'];

  return droppedPeriods
    ? {
        ...cleaned,
        serviceInformation: {
          ...cleaned.serviceInformation,
          servicePeriods: keptPeriods,
        },
      }
    : cleaned;
};

/**
 * The separation date the form should use: only active-duty Veterans with a
 * valid future date have one.
 */
const getSeparationDate = ({ 'view:militaryStatus': status, radDate } = {}) =>
  status === 'active-duty' && isValidDate(radDate) ? radDate : null;

/**
 * Called by the screener pages that hand off to the form. The Veteran's answers
 * decide the claim type, not what an earlier visit to the form left behind, so
 * on mount this rewrites the saved separation date and BDD status from the
 * answers and drops the BDD data that visit added to the form data.
 * @param {Object} data - stored sub-task answers
 * @param {Boolean} isBdd - whether this page hands off a BDD claim
 */
export const useSyncFormBddState = (data, isBdd) => {
  const dispatch = useDispatch();
  const formData = useSelector(state => state?.form?.data);

  useEffect(() => {
    const separationDate = getSeparationDate(data);
    if (separationDate) {
      saveSavedSeparationDate(separationDate, isBdd);
    } else {
      clearSavedSeparationDate();
    }

    const cleaned = removeBddFormData(formData);
    if (cleaned !== formData) {
      dispatch(setData(cleaned));
    }
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
